/**
 * RaceDashboard.js - Race Dashboard Component for Memex Racing
 * 
 * Displays the upcoming race information, map preview, and game status
 * for the "Find the M Token" blind racing game mode.
 */

export class RaceDashboard {
    constructor(uiManager) {
        this.uiManager = uiManager;
        this.isVisible = true;
        this.countdownInterval = null;
        this.timeRemaining = 165; // 2:45 default countdown
        
        // Game state
        this.currentMap = 'Classic Maze';
        this.mapRotation = 1; // Track map rotation count
        this.players = [];
        this.gameStatus = 'waiting'; // waiting, countdown, racing, finished
        
        // Map data
        this.maps = [
            { name: 'Classic Maze', difficulty: 3, color: '#1A3A1A' },
            { name: 'Speed Circuit', difficulty: 1, color: '#222222' },
            { name: 'Serpentine Path', difficulty: 4, color: '#0A0A2A' },
            { name: 'Grid Lock', difficulty: 3, color: '#2A2A2A' },
            { name: 'Spiral Madness', difficulty: 5, color: '#2A0A0A' },
            { name: 'Island Hopper', difficulty: 3, color: '#0A2A2A' }
        ];
        
        console.log('[RaceDashboard] Component initialized');
    }

    /**
     * Initialize the race dashboard
     */
    async initialize() {
        this.createDashboardStructure();
        this.setupEventListeners();
        this.startCountdown();
        console.log('[RaceDashboard] Dashboard initialized');
    }

    /**
     * Create the dashboard HTML structure
     */
    createDashboardStructure() {
        const container = document.getElementById('race-dashboard-container');
        if (!container) {
            const newContainer = document.createElement('div');
            newContainer.id = 'race-dashboard-container';
            newContainer.className = 'race-dashboard-container';
            document.body.appendChild(newContainer);
        }

        const dashboardHTML = `
            <div class="race-dashboard ${this.isVisible ? 'visible' : 'hidden'}">
                <div class="dashboard-header">
                    <div class="dashboard-title">
                        <span class="game-icon">🎮</span>
                        MEMEX RACING - FIND THE M TOKEN
                        <span class="goal-icon">🎯</span>
                    </div>
                </div>
                
                <div class="dashboard-content">
                    <!-- Countdown and Map Info -->
                    <div class="race-info-bar">
                        <div class="countdown-section">
                            <span class="label">GAME STARTS IN:</span>
                            <div class="countdown-timer" id="race-countdown">
                                <span class="timer-icon">⏱️</span>
                                <span class="timer-value">00:02:45</span>
                            </div>
                        </div>
                        <div class="map-info-section">
                            <span class="label">MAP:</span>
                            <span class="map-name" id="current-map">CLASSIC MAZE</span>
                            <span class="map-rotation">(Rotation ${this.mapRotation}/3)</span>
                        </div>
                    </div>
                    
                    <!-- Map Preview Section -->
                    <div class="map-preview-section">
                        <div class="map-preview-header">MAP PREVIEW (Fog of War)</div>
                        <div class="map-preview-canvas" id="map-preview">
                            <div class="map-fog-overlay">
                                <div class="map-content">
                                    <!-- ASCII Map Representation -->
                                    <pre class="ascii-map">
░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
░▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓░
░▓                                        ▓░
░▓  <span class="start-marker">[START]</span>                              ▓░
░▓    <span class="player-spawn">●●●●●●</span> <span class="spawn-info">(All 6 players start here)</span>  ▓░
░▓                                        ▓░
░▓         <span class="unknown">? ? ? ? ? ? ? ? ? ?</span>          ▓░
░▓         <span class="unknown-info">(Unknown maze layout)</span>          ▓░
░▓         <span class="unknown">? ? ? ? <span class="m-token">[M]</span> ? ? ? ?</span>          ▓░
░▓         <span class="token-info">(M token spawns far away)</span>       ▓░
░▓                                        ▓░
░▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓░
░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
                                    </pre>
                                </div>
                            </div>
                        </div>
                        <div class="map-preview-footer">
                            🎯 Goal: Reach M token in 5 minutes (blind movement)
                        </div>
                    </div>
                    
                    <!-- Game Information Grid -->
                    <div class="info-grid">
                        <!-- Game Info -->
                        <div class="info-panel">
                            <div class="panel-title">GAME INFO</div>
                            <div class="panel-content">
                                <div class="info-item">
                                    <span class="info-label">Players:</span>
                                    <span class="info-value" id="player-count">6/6</span>
                                </div>
                                <div class="info-item">
                                    <span class="info-label">Time:</span>
                                    <span class="info-value">5:00 max</span>
                                </div>
                                <div class="info-item">
                                    <span class="info-label">Mode:</span>
                                    <span class="info-value">BLIND RACE</span>
                                </div>
                                <div class="info-item">
                                    <span class="info-label">Boosters:</span>
                                    <span class="info-value status-active">ACTIVE</span>
                                </div>
                                <div class="info-item">
                                    <span class="info-label">Skills:</span>
                                    <span class="info-value status-active">ENABLED</span>
                                </div>
                                <div class="info-item">
                                    <span class="info-label">Chaos:</span>
                                    <span class="info-value chaos-high">HIGH</span>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Racers List -->
                        <div class="info-panel">
                            <div class="panel-title">RACERS (AI)</div>
                            <div class="panel-content racers-list">
                                <div class="racer-item">
                                    <span class="racer-color" style="color: #FF0000;">🟥</span>
                                    <span class="racer-name">Red Racer</span>
                                </div>
                                <div class="racer-item">
                                    <span class="racer-color" style="color: #00FF00;">🟩</span>
                                    <span class="racer-name">Green Speed</span>
                                </div>
                                <div class="racer-item">
                                    <span class="racer-color" style="color: #0000FF;">🟦</span>
                                    <span class="racer-name">Blue Thunder</span>
                                </div>
                                <div class="racer-item">
                                    <span class="racer-color" style="color: #FFFF00;">🟨</span>
                                    <span class="racer-name">Yellow Flash</span>
                                </div>
                                <div class="racer-item">
                                    <span class="racer-color" style="color: #FF00FF;">🟪</span>
                                    <span class="racer-name">Purple Storm</span>
                                </div>
                                <div class="racer-item">
                                    <span class="racer-color" style="color: #00FFFF;">🟧</span>
                                    <span class="racer-name">Cyan Dash</span>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Betting Pool -->
                        <div class="info-panel">
                            <div class="panel-title">BETTING POOL</div>
                            <div class="panel-content">
                                <div class="info-item">
                                    <span class="info-label">Total Pool:</span>
                                    <span class="info-value prize">$5,000</span>
                                </div>
                                <div class="info-item">
                                    <span class="info-label">Winner:</span>
                                    <span class="info-value">Takes All</span>
                                </div>
                                <div class="info-item separator"></div>
                                <div class="info-item">
                                    <span class="info-label">Current Bets:</span>
                                    <span class="info-value" id="bet-count">12</span>
                                </div>
                                <div class="info-item">
                                    <span class="info-label">Avg Bet:</span>
                                    <span class="info-value" id="avg-bet">$416</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Action Buttons -->
                    <div class="action-buttons">
                        <button class="action-btn quick-play" id="btn-quick-play">
                            <span class="btn-icon">🎮</span> QUICK PLAY
                        </button>
                        <button class="action-btn create-room" id="btn-create-room">
                            <span class="btn-icon">🚪</span> CREATE ROOM
                        </button>
                        <button class="action-btn join-room" id="btn-join-room">
                            <span class="btn-icon">🔍</span> JOIN ROOM
                        </button>
                        <button class="action-btn map-select" id="btn-map-select">
                            <span class="btn-icon">🗺️</span> MAP: M
                        </button>
                    </div>
                </div>
            </div>
        `;

        const container2 = document.getElementById('race-dashboard-container');
        container2.innerHTML = dashboardHTML;
    }

    /**
     * Setup event listeners
     */
    setupEventListeners() {
        // Action buttons
        const quickPlayBtn = document.getElementById('btn-quick-play');
        const createRoomBtn = document.getElementById('btn-create-room');
        const joinRoomBtn = document.getElementById('btn-join-room');
        const mapSelectBtn = document.getElementById('btn-map-select');
        
        if (quickPlayBtn) {
            quickPlayBtn.addEventListener('click', () => this.handleQuickPlay());
        }
        
        if (createRoomBtn) {
            createRoomBtn.addEventListener('click', () => this.handleCreateRoom());
        }
        
        if (joinRoomBtn) {
            joinRoomBtn.addEventListener('click', () => this.handleJoinRoom());
        }
        
        if (mapSelectBtn) {
            mapSelectBtn.addEventListener('click', () => this.handleMapSelect());
        }
        
        // Keyboard shortcuts
        document.addEventListener('keydown', (e) => {
            if (e.key === 'M' || e.key === 'm') {
                this.handleMapSelect();
            }
        });
    }

    /**
     * Start countdown timer
     */
    startCountdown() {
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
        }
        
        this.countdownInterval = setInterval(() => {
            this.timeRemaining--;
            this.updateCountdownDisplay();
            
            if (this.timeRemaining <= 0) {
                this.startRace();
            }
            
            // Update urgency at certain thresholds
            if (this.timeRemaining === 30) {
                this.setCountdownUrgency('warning');
            } else if (this.timeRemaining === 10) {
                this.setCountdownUrgency('urgent');
            }
        }, 1000);
    }

    /**
     * Update countdown display
     */
    updateCountdownDisplay() {
        const timerElement = document.querySelector('.countdown-timer .timer-value');
        if (timerElement) {
            const minutes = Math.floor(this.timeRemaining / 60);
            const seconds = this.timeRemaining % 60;
            timerElement.textContent = `00:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        }
    }

    /**
     * Set countdown urgency level
     */
    setCountdownUrgency(level) {
        const countdownElement = document.querySelector('.countdown-timer');
        if (countdownElement) {
            countdownElement.classList.remove('normal', 'warning', 'urgent');
            countdownElement.classList.add(level);
            
            if (level === 'urgent') {
                // Add pulsing animation for urgent
                countdownElement.classList.add('pulse');
            }
        }
    }

    /**
     * Start the race
     */
    startRace() {
        clearInterval(this.countdownInterval);
        this.gameStatus = 'racing';
        
        console.log('[RaceDashboard] Race started!');
        
        if (this.uiManager) {
            this.uiManager.emit('race-started', {
                map: this.currentMap,
                players: this.players
            });
        }
        
        // Update UI to show race in progress
        const timerElement = document.querySelector('.countdown-timer .timer-value');
        if (timerElement) {
            timerElement.textContent = 'RACE IN PROGRESS';
            timerElement.classList.add('racing');
        }
    }

    /**
     * Update race information
     */
    updateRaceInfo(data) {
        // Update player count
        if (data.playerCount !== undefined) {
            const playerCountElement = document.getElementById('player-count');
            if (playerCountElement) {
                playerCountElement.textContent = `${data.playerCount}/6`;
            }
        }
        
        // Update betting pool
        if (data.bettingPool) {
            const betCountElement = document.getElementById('bet-count');
            const avgBetElement = document.getElementById('avg-bet');
            
            if (betCountElement && data.bettingPool.count !== undefined) {
                betCountElement.textContent = data.bettingPool.count;
            }
            
            if (avgBetElement && data.bettingPool.average !== undefined) {
                avgBetElement.textContent = `$${data.bettingPool.average}`;
            }
        }
        
        // Update current map
        if (data.currentMap) {
            this.currentMap = data.currentMap;
            const mapNameElement = document.getElementById('current-map');
            if (mapNameElement) {
                mapNameElement.textContent = this.currentMap.toUpperCase();
            }
        }
    }

    /**
     * Handle quick play button
     */
    handleQuickPlay() {
        console.log('[RaceDashboard] Quick play clicked');
        if (this.uiManager) {
            this.uiManager.emit('quick-play-requested');
        }
    }

    /**
     * Handle create room button
     */
    handleCreateRoom() {
        console.log('[RaceDashboard] Create room clicked');
        if (this.uiManager) {
            this.uiManager.emit('create-room-requested');
        }
    }

    /**
     * Handle join room button
     */
    handleJoinRoom() {
        console.log('[RaceDashboard] Join room clicked');
        if (this.uiManager) {
            this.uiManager.emit('join-room-requested');
        }
    }

    /**
     * Handle map selection
     */
    handleMapSelect() {
        console.log('[RaceDashboard] Map select clicked');
        if (this.uiManager) {
            this.uiManager.emit('map-select-requested');
        }
        
        // Cycle through maps (for demo)
        const currentIndex = this.maps.findIndex(m => m.name === this.currentMap);
        const nextIndex = (currentIndex + 1) % this.maps.length;
        this.currentMap = this.maps[nextIndex].name;
        
        // Update display
        const mapNameElement = document.getElementById('current-map');
        if (mapNameElement) {
            mapNameElement.textContent = this.currentMap.toUpperCase();
        }
        
        // Update rotation counter
        if (nextIndex === 0) {
            this.mapRotation++;
            const rotationElement = document.querySelector('.map-rotation');
            if (rotationElement) {
                rotationElement.textContent = `(Rotation ${this.mapRotation}/3)`;
            }
        }
    }

    /**
     * Reset countdown
     */
    resetCountdown(seconds = 165) {
        this.timeRemaining = seconds;
        this.gameStatus = 'countdown';
        this.startCountdown();
    }

    /**
     * Show/hide dashboard
     */
    toggle() {
        this.isVisible = !this.isVisible;
        const dashboard = document.querySelector('.race-dashboard');
        if (dashboard) {
            dashboard.classList.toggle('visible', this.isVisible);
            dashboard.classList.toggle('hidden', !this.isVisible);
        }
    }

    /**
     * Cleanup
     */
    destroy() {
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
        }
        
        const container = document.getElementById('race-dashboard-container');
        if (container) {
            container.remove();
        }
    }
}

// Export as ES module
export default RaceDashboard;